import type { Stage } from '../kit/stage';
import type { Department } from '../user/user';
import type { Permission, Role } from './role';

/** Who is asking to work a bench: the four facts the rule reads. */
export interface BenchWorker {
  role: Role;
  department: Department;
  isIntakeOperator: boolean;
  permissions: readonly Permission[];
}

/**
 * Whether this person may work this bench. Section 7.10 item 4, the server's
 * WorksThisBench, stated once on this side so the job view, the mock server and
 * the assignee picker read the same rule and cannot drift from each other.
 *
 * The tests are in this order for a reason. Management works every bench,
 * because the owner and the QC reviewer do. Intake is a desk, not a bench, and
 * is tested before the refusal below: whoever may create a case may start it,
 * which is the intake permission, and the operator flag is the second door for
 * a technician who covers the desk without holding it. Final Check asks for its
 * own permission, and asks for it only at Final Check: holding it is a licence
 * to pass or fail work, not a key to every other bench. It is tested before the
 * refusal below as well, so that there the permission decides and not the role:
 * a receptionist or an accountant given "Pass / fail Final Check" works Final
 * Check as anybody else who holds it does. They used to be refused first, and so
 * were told to ask management to assign them a case they had been given the
 * access to decide. The lab's report of 9 October 2026. Reception and Accounts
 * work no other production bench. Everybody else must be standing at their own
 * station.
 */
export function worksThisBench(person: BenchWorker, stage: Stage): boolean {
  if (person.role === 'management') return true;
  if (stage === 'intake') return person.permissions.includes('intake') || person.isIntakeOperator;
  if (stage === 'finalcheck') return person.permissions.includes('finalcheck');
  if (person.role === 'reception' || person.role === 'accountant') return false;
  return person.department === stage;
}

/**
 * Whether this person may work this case: their own bench, or a case given to
 * them by name. A case may be assigned to anybody in the lab, not only to the
 * people who work its bench (request of 5 October 2026), and the person it was
 * given to can then start it, finish it and flag it wherever it sits. The
 * server's BenchRules.WorksThisCase.
 */
export function worksThisCase(
  worksBench: boolean,
  assignedToUserId: string | null,
  userId: string,
): boolean {
  return worksBench || (assignedToUserId !== null && assignedToUserId === userId);
}

/**
 * The refusals of `POST /kits/{kitId}/assign`, worded once. The server
 * says them, the mock says them the same way, and a screen renders whichever
 * arrives verbatim. Section 1.4 of the request of 13 September 2026.
 */
export const ASSIGN_REFUSAL = {
  notInSystem: 'That person is not in the system.',
  /*
   * A case on hold is a case the lab has stopped, usually over money. Handing
   * it to somebody is the opposite of stopping it, and the person handed it has
   * no way to know it was stopped. Release the hold, then assign it.
   */
  onHold: 'This case is on hold and cannot be given to anybody.',
} as const;
